import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { saveFavorite, deleteFavorite, searchLocations, getLocationById, getUserSaveData, saveFilters, createLead, getFeedback, createFeedback } from '../services/api';
import { STATUS } from '../types';


// Initial State
const initialState = {
  locations: [],
  filteredLocations: [],
  selectedLocation: null,
  searchCenter: null,
  isLoading: false,
  filters: {
    [STATUS.ACTIVE]: true,
    [STATUS.ACTIVE_OLDER]: true,
    [STATUS.PROSPECT]: true,
    [STATUS.TARGET]: true,
    myAccountOnly: false,
  },
  searchRadius: 3,
  recentlyViewed: [],
  searchHistory: [],
  currentSearchTerm: '',
  isSearchVisible: true,
  isHomeVisible: false,
  error: null,
  hasHydrated: false,
  showFilters: false,
  showNeighborhoods: true,
  polygons: [],
  polygonsLoading: false,
  showLeads: true,
  userPrefsLoaded: false,
  favouritesIds: [],
  serverFavoritesRaw: [],
  isFavoritesVisible: false,
  salesforceBaseUrl: 'https://sysco--training.sandbox.lightning.force.com',
  isLeadFormOpen: false,
  leadFormDraft: null,
  leadSubmitStatus: 'idle',    // 'idle' | 'submitting' | 'success' | 'error'
  leadSubmitError: '',
  leadSuccessModal: false,
  userMeta: {
    managerId: '',
    dsmName: '',
    title: '',
    name: '',
    Id: '',
  },
  controlsOpen: true,


};

const htmlToText = (s = '') => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(s, 'text/html');
  const name = doc.body.textContent.trim();
  console.log(name); // "Christopher Lerch"
  return name;
}

const parseAddressPartsForStore = (addr = '') => {
  const parts = String(addr || '').split(',').map(s => s.trim());
  const street = parts[0] || '';
  const city = parts[1] || '';
  let state = '', postalCode = '', country = '';
  if (parts[2]) {
    const m = parts[2].match(/([A-Za-z]{2})\s+(\d{4,10}(?:-\d{4})?)/);
    if (m) { state = m[1]; postalCode = m[2]; } else { state = parts[2]; }
  }
  if (parts[3]) country = parts[3];
  return { street, city, state, postalCode, country };
};


const getLocationId = (location) => {
  if (!location) return null;

  // Check each field in priority order, ensuring it's a valid non-N/A string
  const id = (location?.id && location.id.trim() !== 'N/A' && location.id.trim()) ||
    (location?.sfId && location.sfId.trim() !== 'N/A' && location.sfId.trim()) ||
    (location?.accountId && location.accountId.trim() !== 'N/A' && location.accountId.trim()) ||
    (location?.accountDetails?.accountId && location.accountDetails.accountId.trim() !== 'N/A' && location.accountDetails.accountId.trim()) ||
    (location?.placeId && location.placeId.trim() !== 'N/A' && location.placeId.trim()) ||
    (location?.name) || null;

  if (!id) return null;

  // Handle special scopes first
  if (location.scope === 'GOOGLE') {
    return { id, type: 'placeId' };
  }
  if (location.scope === 'TARGET SALESFORCE') {
    return { id, type: 'leadId' };
  }

  // For other scopes, determine type based on which field matched
  let type = 'id';
  if (id === location?.sfId?.trim()) type = 'sfid';
  else if (id === location?.accountId?.trim()) type = 'accountId';
  else if (id === location?.accountDetails?.accountId?.trim()) type = 'acctId';
  else if (id === location?.placeId?.trim()) type = 'placeId';
  else if (id === location?.name) type = 'name';

  return { id, type };
};

const searchByAccountId = async (accountId) => {
  return await getLocationById(accountId, 'accountId');
};

const flattenFilters = (filtersObj) => {
  const result = {};
  Object.entries(filtersObj).forEach(([key, value]) => {
    result[key] = Array.isArray(value) ? value.join(',') : value;
  });
  return result;
};

const isSalesforceLead = (location) => {
  const status = (location.shipStatus || location.status || '').toLowerCase();
  return (status === 'target') && location.scope === 'SALESFORCE';
};

const industry = [
  { label: 'Restaurants & Bars', value: 'Restaurants & Bars', icon: 'assets/industries/Restaurants.svg' },
  { label: 'Healthcare', value: 'healthcare', icon: 'assets/industries/Healthcare.svg' },
  { label: 'Retail', value: 'RETAIL OUTLETS-NON-GROCERY', icon: 'assets/industries/Retail.svg' },
  { label: 'Travel & Leisure', value: 'Travel & Leisure', icon: 'assets/industries/Travel.svg' },
  { label: 'Education', value: 'education', icon: 'assets/industries/Education.svg' },
  { label: 'Government', value: 'GOVERNMENT EMPLOYMENT', icon: 'assets/industries/Government.svg' },
  { label: 'Other', value: 'All Other Foodservice', icon: 'assets/industries/Other.svg' },
];
// External reference for hydration
let setHasHydratedGlobal = () => { };

// ---- Favorites helpers ------------------------------------

// Robust unique key for a SERVER FAVORITE record (server side)
const normalizeFavKey = (fav) => {
  const cand = [
    fav?.accountId,
    fav?.id,
    fav?.placeId,
    fav?.sfId,
    fav?.sfid,
    fav?.acctId,
  ].find(v => typeof v === 'string' && v.trim());
  if (cand) return cand.trim();
  return fav?.accountName ? `name:${fav.accountName.toLowerCase().trim()}` : null;
};

const getFavoritePayload = (loc) => {
  const isTarget = (loc) => ((loc.shipStatus ?? loc.status ?? '').toString().toLowerCase() === 'target');
  const isSalesforceLead = (loc) => isTarget(loc) && String(loc.scope ?? '').trim().toUpperCase() === 'SALESFORCE';
  const accountType = isSalesforceLead(loc) ? 'TARGET SALESFORCE' : (isTarget(loc) ? 'GOOGLE' : (loc.scope ?? ''));
  console.log("accountType : favorites ", accountType)
  return {
    accountId: getLocationId(loc)?.id,
    accountType: accountType,
    accountName: loc?.name || loc?.accountDetails?.name || 'Unknown',
    accountStatus: loc?.shipStatus || loc?.status,
    latitude: loc?.lat ?? 0,
    longitude: loc?.lng ?? 0,
  };
};

export const useMapStore = create(
  persist(
    (set, get) => {
      return {
        ...initialState,
        // Save hydration trigger
        setHasHydratedGlobal: () => set({ hasHydrated: true }),

        getLocationId,

        setShowFilters: (visible) =>
          set({ showFilters: visible }),

        setShowNeighborhoods: async (value) => set({ showNeighborhoods: value }),

        setSalesforceBaseUrl: (url) => set(() => ({ salesforceBaseUrl: url })),
        globalSearchMode: 'location', // for sorting
        setGlobalSearchMode: (mode) => set({ globalSearchMode: mode }),
        GlobalDeviceLocation: null, // for sorting
        setGlobalDeviceLocation: (position) => set({ GlobalDeviceLocation: position }),
        GlobalMapCenter: null,
        setGlobalMapCenter: (position) => set({ GlobalMapCenter: position }),

        openControls: () => set({ controlsOpen: true }),
        closeControls: () => set({ controlsOpen: false }),
        toggleControls: () => set((s) => ({ controlsOpen: !s.controlsOpen })),
        controlsIntent: null,                 // null | 'bookmarks' | ... 
        setControlsIntent: (v) => set({ controlsIntent: v }),
        bookmarksActive: false,
        setBookmarksActive: (v) => set({ bookmarksActive: v }),

        feedbackById: {},          // { [id]: Array<feedbackRow> }
        feedbackLoadingById: {},   // { [id]: boolean }
        feedbackErrorById: {},     // { [id]: string | null }

        // Fetch all feedback rows for a given placeId / sfId
        fetchFeedback: async (id) => {
          if (!id) return;

          console.log("[feedback] fetchFeedback start:", id);
          set((s) => ({
            feedbackLoadingById: { ...s.feedbackLoadingById, [id]: true },
            feedbackErrorById: { ...s.feedbackErrorById, [id]: null },
          }));

          try {
            const res = await getFeedback({ id }); // { success, data: [...], error, timeStamp }
            const rows = Array.isArray(res?.data) ? res.data : [];

            console.log("[feedback] fetchFeedback ok:", id, "rows:", rows.length);

            set((s) => ({
              feedbackById: { ...s.feedbackById, [id]: rows },
              feedbackLoadingById: { ...s.feedbackLoadingById, [id]: false },
            }));

            return rows;
          } catch (err) {
            console.error("[feedback] fetchFeedback failed:", id, err);

            set((s) => ({
              feedbackLoadingById: { ...s.feedbackLoadingById, [id]: false },
              feedbackErrorById: { ...s.feedbackErrorById, [id]: err?.message || "Failed to load feedback" },
            }));

            return null;
          }
        },

        // Submit feedback (POST) and refresh list for that id
        submitFeedback: async ({ id, name, scope, reason, comment }) => {
          try {
            console.log("[feedback][store] submit start", { id, scope, reason });

            // optional: set a per-id loading flag
            set((s) => ({
              feedbackLoadingById: { ...(s.feedbackLoadingById || {}), [id]: true },
            }));

            await createFeedback({ id, name, scope, reason, comment });

            // refresh list after POST
            const res = await getFeedback({ id }); // { success, data: [...] }
            const rows = Array.isArray(res?.data) ? res.data : [];

            set((s) => ({
              feedbackById: { ...(s.feedbackById || {}), [id]: rows },
              feedbackLoadingById: { ...(s.feedbackLoadingById || {}), [id]: false },
            }));

            console.log("[feedback][store] submit done", { id, count: rows.length });
            return res;
          } catch (err) {
            set((s) => ({
              feedbackLoadingById: { ...(s.feedbackLoadingById || {}), [id]: false },
            }));
            console.error("[feedback][store] submit failed", err);
            throw err;
          }
        },

        showBookmarksOnly: false,
        setShowBookmarksOnly: (on) => {
          console.log('[bookmarksOnly] set →', on);
          useMapStore.setState({ showBookmarksOnly: !!on });
        },

        isFavorite: (location) => {
          const key = getLocationId(location)?.id;
          if (!key) return false;
          const favs = useMapStore.getState().favoritesIds;
          // console.debug('[fav] isFavorite?', { name: location?.name});
          return Array.isArray(favs) ? favs.includes(key) : false;
        },

        toggleFavorite: async (location) => {
          console.log('[fav][toggleFavorite] start for', location?.name);
          const key = getLocationId(location)?.id;
          console.log('[fav][toggleFavorite] key =', key);
          if (!key) return;

          const state = useMapStore.getState();
          const current = Array.isArray(state.favoritesIds) ? state.favoritesIds : [];
          const prevRaw = Array.isArray(state.serverFavoritesRaw) ? state.serverFavoritesRaw : [];

          console.log('current and key', current, key)
          const isFav = current.includes(key);
          const next = isFav ? current.filter(k => k !== key) : [...current, key];
          console.log('[fav][toggleFavorite] isFav?', isFav, 'currentCount=', current.length, '→ nextCount=', next.length);


          // optimistic UI
          useMapStore.setState({ favoritesIds: next });
          console.log('[fav][toggleFavorite] optimistic set applied', useMapStore.getState().favoritesIds);


          try {
            if (isFav) {
              console.log('[fav][toggleFavorite] DELETE flow → calling deleteFavorite for', key);
              await deleteFavorite(key);
              console.log('[fav][toggleFavorite] DELETE success');

              // --- ADDED: keep serverFavoritesRaw in sync so Saved list updates immediately
              const prevList = Array.isArray(useMapStore.getState().serverFavoritesRaw) ? useMapStore.getState().serverFavoritesRaw : [];
              console.log('[FAVORITES DEBUG] Before DELETE - serverFavoritesRaw:', prevList);
              console.log('[FAVORITES DEBUG] Removing favorite with key:', key);

              useMapStore.setState((s) => {
                const newList = (Array.isArray(s.serverFavoritesRaw) ? s.serverFavoritesRaw : [])
                  .filter(r => getLocationId(r)?.id !== key);
                console.log('[FAVORITES DEBUG] After DELETE - serverFavoritesRaw:', newList);
                return { serverFavoritesRaw: newList };
              });
              // -------------------------------------------------------

            } else {
              console.log('calling payload');
              const payload = getFavoritePayload(location); // your existing helper
              console.log('[fav][toggleFavorite] POST flow → payload =', payload);
              await saveFavorite(payload);
              console.log('[fav][toggleFavorite] POST success');

              // --- ADDED: optimistic insert into serverFavoritesRaw (if not already present)
              useMapStore.setState((s) => {
                const list = Array.isArray(s.serverFavoritesRaw) ? s.serverFavoritesRaw : [];
                const exists = list.some(r => getLocationId(r)?.id === key);
                if (exists) return {};
                const shadow = {
                  // minimal fields matching backend shape
                  accountId: getLocationId(location)?.id,
                  accountName: location?.name || '',
                  accountStatus: (location?.shipStatus || location?.status || 'prospect'),
                  latitude: location?.lat ?? null,
                  longitude: location?.lng ?? null,
                  accountType: location?.scope || '',
                  // createdAt could be added, but not required for display
                };
                return { serverFavoritesRaw: [shadow, ...list] };
              });
              // -------------------------------------------------------
            }
          } catch (e) {
            // revert on failure (unchanged)
            console.error('[fav][toggleFavorite] API failed, reverting. error =', e);
            useMapStore.setState({ favoritesIds: current });
            console.warn('[toggleFavorite] failed:', e);
          }
        },

        // Build a UI-ready array of favorite locations.
        // It tries to find a full object from current results/recentlyViewed,
        // and falls back to the raw server record shape if needed.
        getFavoritesList: () => {
          const {
            favoritesIds = [],
            serverFavoritesRaw = [],
            locations = [],
            filteredLocations = [],
            recentlyViewed = [],
            favoriteKeyFor,
          } = get();

          // fast lookup of known “rich” location objects
          const pool = [...filteredLocations, ...locations, ...recentlyViewed];
          const byKey = new Map();
          for (const loc of pool) {
            const k = getLocationId(loc)?.id;
            if (k && !byKey.has(k)) byKey.set(k, loc);
          }

          // fallback mapper for server rows → location-like
          const toUiLoc = (r) => ({
            accountId: getLocationId(r).id || null,
            name: r.accountName || r.name || '',
            address: r.address || '',
            lat: r.latitude ?? r.lat ?? null,
            lng: r.longitude ?? r.lng ?? null,
            status: r.shipStatus || r.accountStatus || r.status || '',
            shipStatus: (r.accountStatus || r.status || '').toUpperCase(),
            scope: r.accountType || '',
          });

          // Make a single deduped list following favoritesIds order
          const out = [];
          const seen = new Set();

          for (const key of favoritesIds) {
            if (!key || seen.has(key)) continue;

            // Prefer a “rich” object if present
            const rich = byKey.get(key);
            if (rich) {
              out.push(rich);
              seen.add(key);
              continue;
            }

            // Otherwise, try to match a server favorite row by the same key
            const raw = serverFavoritesRaw.find((r) => {
              const cand = getLocationId(r)?.id;
              return cand === key;
            });

            if (raw) {
              out.push(toUiLoc(raw));
              seen.add(key);
            }
          }

          return out;
        },



        favoriteKeyFor: (location) => getLocationId(location)?.id,


        // ── LOAD SAVED PREFS (runs once) ──────────────────────────────
        loadUserPrefs: async () => {
          if (get().userPrefsLoaded) return;
          console.log('[loadUserPrefs] starting…');

          try {
            const res = await getUserSaveData();
            console.log('[loadUserPrefs] raw:', res);

            if (!res?.success) throw new Error(res?.error || 'request failed');

            const { filters, favorites, searches, recents } = res.data || {};

            // ---- Filters → normalize into filtersState (arrays) ----
            if (filters && typeof filters === 'object') {
              const toArr = (v) => {
                if (v == null) return undefined;
                if (Array.isArray(v)) return v;
                if (typeof v === 'string' && v.trim() !== '')
                  return v.split(',').map(s => s.trim()).filter(Boolean);
                return undefined;
              };

              const normalized = {
                acctype: toArr(filters.acctype),
                businessUnit: toArr(filters.businessUnit),
                cuisine: toArr(filters.cuisine),
                industry: toArr(filters.industry),
                yourWay: toArr(filters.yourWay),
                perks: toArr(filters.perks),
                csScore: toArr(filters.csScore),
              };

              const cleaned = Object.fromEntries(
                Object.entries(normalized).filter(([, v]) => Array.isArray(v) && v.length)
              );

              if (Object.keys(cleaned).length) {
                set((state) => ({ filtersState: { ...state.filtersState, ...cleaned } }));
                console.log('[loadUserPrefs] applied filtersState:', cleaned);
              }
            }

            // ---- User Info → store DSM name, manager, title, name ----
            const ui = res.data?.userInfo;
            if (ui) {
              set({
                userMeta: {
                  Id: ui.Id || '',
                  managerId: ui.ManagerId || '',
                  dsmName: htmlToText(ui.DSMName__c || ''),
                  title: ui.Title || '',
                  name: ui.Name || '',
                }
              });
              console.log('[loadUserPrefs] userMeta set from userInfo:', ui);
            }

            // ---- Favorites → store ids for quick heart checks ----

            if (Array.isArray(favorites) && favorites.length) {
              console.log('[FAVORITES DEBUG] Loading favorites from server:', favorites);

              // Transform favorites to match the shadow structure used in toggleFavorite
              const transformedFavorites = favorites.map(fav => ({
                accountId: getLocationId(fav)?.id,
                accountName: fav.accountName || '',
                accountStatus: fav.accountStatus || fav.status || 'prospect',
                latitude: fav.latitude ?? fav.lat ?? null,
                longitude: fav.longitude ?? fav.lng ?? null,
                accountType: fav.accountType || fav.scope || '',
              })).filter(f => f.accountId); // Filter out invalid entries

              console.log('This is running 1', transformedFavorites);

              if (transformedFavorites.length) {
                console.log('This is running 2', transformedFavorites);

                let newState;
                try {
                  // Get current state first
                  const currentState = get();
                  console.log('Debug: Current state:', {
                    hasFavoritesIds: !!currentState.favoritesIds,
                    hasServerFavoritesRaw: !!currentState.serverFavoritesRaw
                  });

                  // Step 1: Try creating the favoritesIds array
                  console.log('Debug: Creating favoritesIds');
                  const newFavoriteIds = Array.from(new Set([
                    ...transformedFavorites.map(f => {
                      console.log('Debug: Mapping favorite:', f);
                      return f.accountId;
                    }),
                    ...(Array.isArray(currentState.favoritesIds) ? currentState.favoritesIds : [])
                  ]));
                  console.log('Debug: Created favoritesIds:', newFavoriteIds);

                  // Step 2: Try creating the serverFavoritesRaw array
                  console.log('Debug: Creating serverFavoritesRaw');
                  const newServerFavorites = Array.isArray(currentState.serverFavoritesRaw)
                    ? [...transformedFavorites, ...currentState.serverFavoritesRaw]
                    : transformedFavorites;
                  console.log('Debug: Created serverFavoritesRaw:', newServerFavorites.length);

                  // Step 3: Combine into new state
                  newState = {
                    favoritesIds: newFavoriteIds,
                    serverFavoritesRaw: newServerFavorites
                  };
                  console.log('Debug: Created complete state object');
                } catch (error) {
                  console.error('Debug: Error in state creation:', error);
                  throw error;
                }

                set((state) => {
                  console.log('Debug: Setting state');
                  return newState;
                });

                // Log the current state after the update
                const currentState = get();
                console.log('This is running 3: Current state:', {
                  favoritesCount: currentState.favoritesIds?.length || 0,
                  serverFavoritesCount: currentState.serverFavoritesRaw?.length || 0
                });
              }

              console.log('This is running 4: Finished processing favorites');
            }

            // ---- Searches → map to rich UI shape & merge (dedupe by type+value; newest on top) ----
            if (Array.isArray(searches)) {
              set((state) => {
                const keyOf = (t, v) =>
                  `${String(t).toLowerCase().trim()}|${String(v).toLowerCase().trim()}`;

                // Sort server items newest → oldest (fallback to original order)
                const sorted = [...searches].sort((a, b) => {
                  const ta = new Date(a?.createdAt || 0).getTime();
                  const tb = new Date(b?.createdAt || 0).getTime();
                  return tb - ta;
                });

                let next = Array.isArray(state.searchHistory) ? state.searchHistory : [];

                sorted.forEach((s, idx) => {
                  const type = String(s.searchType || '');
                  const value = String(s.searchValue || '');
                  const k = keyOf(type, value);

                  const entry = {
                    id: `${Date.now()}-${idx}`,                 // UI key only
                    term: value,                                 // display-only (NO prefix)
                    searchType: type,                            // preserve backend
                    searchValue: value,                          // preserve backend
                    timestamp: s.createdAt || new Date().toISOString(),
                    center: null,                                // backend doesn't send center
                    radius: null,
                    filters: {},                                 // backend not providing filters here
                  };

                  // Remove any existing with same (type,value), then put this one at the top
                  next = [
                    entry,
                    ...next.filter(e => keyOf(e.searchType || 'Location', e.searchValue || e.term) !== k)
                  ];
                });

                return { searchHistory: next.slice(0, 10) };
              });
              console.log('[loadUserPrefs] merged searchHistory from backend (deduped by type+value; term=value only)');
            }


            // ---- Recents → map to location shape & merge/dedupe ----
            if (Array.isArray(recents)) {
              console.log('[loadUserPrefs] processing recents:', recents);
              set((state) => {
                const toLoc = (r) => ({
                  // give getLocationId something to grab
                  accountId: getLocationId(r).id ?? null,
                  name: r.accountName,
                  lat: r.latitude ?? null,
                  lng: r.longitude ?? null,
                  status: r.accountStatus,
                  shipStatus: (r.accountStatus || '').toUpperCase(), // aligns with UI filters
                  scope: r.accountType,
                });

                const fromServer = recents.map(toLoc);

                // merge with existing recentlyViewed; keep first occurrence
                const byId = new Map();
                [...fromServer, ...state.recentlyViewed].forEach((loc) => {
                  const id =
                    (getLocationId(loc)?.id) ||
                    loc.accountId ||
                    loc.id;
                  if (!id) return;
                  if (!byId.has(id)) byId.set(id, loc);
                });

                const merged = Array.from(byId.values()).slice(0, 5);
                return { recentlyViewed: merged };
              });
              console.log('[loadUserPrefs] merged recentlyViewed with server recents');
            }

          } catch (e) {
            console.warn('[loadUserPrefs] failed, using defaults', e);
          } finally {
            set({ userPrefsLoaded: true });
          }
        },




        // ── SAVE CURRENT FILTERS AS DEFAULT ───────────────────────────
        saveCurrentAsDefault: async (filtersObj) => {
          try {
            // build query-string from the object we just received
            const qs = new URLSearchParams();
            Object.entries(filtersObj).forEach(([k, v]) =>
              qs.append(k, Array.isArray(v) ? v.join(',') : v)
            );

            const res = await saveFilters(qs.toString());   // helper in api.js
            if (res.success) {
              console.log('[saveCurrentAsDefault] saved');
              set({ filterSaveMsg: 'Saved!' });
              setTimeout(() => set({ filterSaveMsg: '' }), 1500);
            } else {
              console.warn('[saveCurrentAsDefault]', res.error);
            }
          } catch (err) {
            console.error('[saveCurrentAsDefault]', err);
          }
        },

        openLeadForm: (fromLocation) => {
          try {
            const { street, city, state, postalCode, country } =
              parseAddressPartsForStore(fromLocation?.address || '');

            const draft = {
              // Required/core
              Company: fromLocation?.accountDetails?.accountName || fromLocation?.name || '',
              FirstName: '',
              LastName: '',
              Category__c: '',                           // no default per your list (you can change)
              Assign_to_DSM__c: '',                      // lookup → just a text field for now
              Status: 'Open',                            // default
              //Rating: '',                                // Hot, Warm, Cold, Ready to Buy
              Expected_Account_Type__c: '',              // TRS, TRP, LCC, CMU, Bid & Oth
              Business_Structure__c: '',                 // Local, Corporate
              CurrencyIsoCode: 'USD - U.S.Dollar',       // default
              Street: street,
              City: city,
              State: state,
              PostalCode: postalCode,
              Country: country || 'USA',
              RecordTypeId: '012E0000000V4MQIA0',
            };

            console.log('[lead][ui] open', draft);
            set({ isLeadFormOpen: true, leadFormDraft: draft, leadSubmitStatus: 'idle', leadSubmitError: '' });
          } catch (e) {
            console.error('[lead][ui] open failed', e);
          }
        },

        closeLeadForm: () => {
          console.log('[lead][ui] close');
          set({ isLeadFormOpen: false, leadFormDraft: null, leadSubmitStatus: 'idle', leadSubmitError: '' });
        },

        updateLeadDraft: (patch) => {
          set((s) => {
            const next = { ...(s.leadFormDraft || {}), ...patch };
            console.log('[lead][ui] change', patch);
            return { leadFormDraft: next };
          });
        },

        submitLead: async () => {
          const { leadFormDraft } = get();
          if (!leadFormDraft) return;

          try {
            console.log('[lead][ui] submit clicked');
            set({ leadSubmitStatus: 'submitting', leadSubmitError: '' });

            const { leadFormDraft, userMeta } = useMapStore.getState();
            if (!leadFormDraft) return;
            const selectedLocation = useMapStore.getState();

            const cleanValue = (v) => (v === '--None--' ? undefined : v);

            if (!leadFormDraft.Email && !leadFormDraft.Phone && !leadFormDraft.MobilePhone) {
              set({ leadSubmitStatus: 'error', leadSubmitError: 'Lead should have at least one communication information to create new lead (Phone/Email/Mobile)' });
              return;
            }

            const normalizeUSPhone = (v) => {
              const digits = String(v || '').replace(/\D/g, '');
              if (digits.length === 10) return `+1${digits}`;
              if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
              return '';
            };

            const payload = {
              OwnerId: userMeta?.managerId,
              Company: leadFormDraft.Company,
              FirstName: leadFormDraft.FirstName,
              LastName: leadFormDraft.LastName,
              CurrencyIsoCode: 'USD',
              Email: leadFormDraft.Email || '',
              Phone: normalizeUSPhone(leadFormDraft.Phone) || '',
              MobilePhone: normalizeUSPhone(leadFormDraft.MobilePhone) || '',
              Status: 'Open',
              Street: leadFormDraft.Street,
              City: leadFormDraft.City,
              State: leadFormDraft.State,
              PostalCode: leadFormDraft.PostalCode,
              Country: leadFormDraft.Country || 'USA',
              RecordTypeId: '012E0000000V4MQIA0',

              Category__c: cleanValue(leadFormDraft.Category__c),
              Assign_to_DSM__c: userMeta?.managerId || '', // assign to manager if available
              Expected_Account_Type__c: cleanValue(leadFormDraft.Expected_Account_Type__c),
              Potential_Average_Weekly_Sales__c: leadFormDraft.Potential_Avg_Wkly_Sales__c
                ? parseInt(leadFormDraft.Potential_Avg_Wkly_Sales__c, 10)
                : undefined,

              Sales_Call_Plan_Day__c: cleanValue(leadFormDraft.Sales_Call_Plan_Day__c),
              Sales_Call_Plan_Time__c: cleanValue(leadFormDraft.Sales_Call_Plan_Time__c),

              Business_Structure__c: 'Local',
              CHD_Type_Of_Operation__c: cleanValue(leadFormDraft.Industry),
              CHD_Local_Type_of_Operation__c: cleanValue(leadFormDraft.Industry_Detail__c),
              Cuisine_Level_2__c: cleanValue(leadFormDraft.Primary_Menu_Type__c),
              Cuisine_Level_1__c: cleanValue(leadFormDraft.Cuisine_Segment_Type__c),

              Latitude: selectedLocation?.lat,
              longitude: selectedLocation?.lng,
            };
            console.log('[lead][ui] submit payload', payload);

            useMapStore.setState({ leadSubmitStatus: 'submitting', leadSubmitError: '' });

            const res = await createLead(payload);

            if (!res?.success) throw new Error(res?.error || 'Lead creation failed');

            console.log('[lead][ui] submit success', res.data?.id);
            set({ leadSubmitStatus: 'success', leadSuccessModal: true });


            try {
              const {
                selectedLocation,
                searchCenter,
                searchByLatLng,
              } = useMapStore.getState();

              // Prefer precise coordinates if we have them (where the form was opened)
              if (selectedLocation?.lat && selectedLocation?.lng) {
                await searchByLatLng(selectedLocation.lat, selectedLocation.lng);
              }
              // Fallback to the last map center if available
              else if (searchCenter?.lat && searchCenter?.lng) {
                await searchByLatLng(searchCenter.lat, searchCenter.lng);
              }
            } catch (e) {
              console.warn('[lead][ui] map refresh after create failed (non-blocking)', e);
            }

            useMapStore.setState({
              leadFormDraft: null,
              leadSubmitStatus: 'idle',
              selectedLocation: null,       // closes the Location Details panel
            });
            // setSearchVisible(false);        // hides the sidebar/details sheet (esp. on mobile)


            // Optional: add to recents
            // const { saveRecent } = await import('../services/api');
            // saveRecent({
            //   accountId: res.data?.id || leadFormDraft.Company,
            //   accountName: leadFormDraft.Company,
            //   accountStatus: 'target',
            //   accountType: 'TARGET SALESFORCE',
            //   latitude: null, longitude: null,
            // });

            // Close after a beat so user sees success state
            setTimeout(() => {
              set({ isLeadFormOpen: false, leadFormDraft: null, leadSubmitStatus: 'idle', setSearchVisible: false });
            }, 300);
          } catch (e) {
            console.error('[lead][ui] submit error', e);
            set({ leadSubmitStatus: 'error', leadSubmitError: e?.message || 'Unknown error' });
          }
          set({ leadSuccessModal: false });
        },

        filtersState: {
          acctype: ['lcc', 'oth', 'trs'],
          industry: industry.map(i => i.value),
          cuisine: ['African', 'American', 'Americanized Chinese', 'Authentic Chinese', 'Bakery', 'Bar', 'BBQ', 'Beverages', 'Brewery', 'Burgers', 'Cafe', 'Cajun/Creole', 'Caribbean', 'Chicken Wings', 'Desserts', 'Diner', 'European', 'French', 'German', 'Hawaiian', 'Hot Dogs', 'Indian', 'Irish', 'Italian', 'Japanese', 'Korean', 'Med or Mid', 'Mexican', 'Other', 'Other Asian', 'Pizza', 'Salad', 'Sandwich', 'Seafood', 'Snacks', 'South American', 'Southern/Soulfood', 'Steak', 'Sushi', 'Tacos', 'Thai', 'Vietnamese'],
          businessUnit: ['US Broadline', 'Freshpoint', 'SSMG', 'Hawaii', 'Asian Foods', 'European Imports', 'Guest Supply', 'Canada'],
          perks: ['Enrolled', 'notEnrolled'],       // not enrolled by default
          yourWay: ['Enrolled', 'notEnrolled'],     // not enrolled by default
          csScore: ['New', '1', '2', '3', '4', '5'],
        },
        setFiltersState: (updates) =>
          set((state) => ({
            filtersState: { ...state.filtersState, ...updates },
          })),
        clearFiltersState: () => set({
          filtersState: {
            acctype: ['lcc', 'oth', 'trs'],
            industry: industry.map(i => i.value),
            cuisine: ['African', 'American', 'Americanized Chinese', 'Authentic Chinese', 'Bakery', 'Bar', 'BBQ', 'Beverages', 'Brewery', 'Burgers', 'Cafe', 'Cajun/Creole', 'Caribbean', 'Chicken Wings', 'Desserts', 'Diner', 'European', 'French', 'German', 'Hawaiian', 'Hot Dogs', 'Indian', 'Irish', 'Italian', 'Japanese', 'Korean', 'Med or Mid', 'Mexican', 'Other', 'Other Asian', 'Pizza', 'Salad', 'Sandwich', 'Seafood', 'Snacks', 'South American', 'Southern/Soulfood', 'Steak', 'Sushi', 'Tacos', 'Thai', 'Vietnamese'],
            businessUnit: ['US Broadline', 'Freshpoint', 'SSMG', 'Hawaii', 'Asian Foods', 'European Imports', 'Guest Supply', 'Canada'],
            perks: ['Enrolled', 'notEnrolled'],
            yourWay: ['Enrolled', 'notEnrolled'],
            csScore: ['New', '1', '2', '3', '4', '5'],

          }
        }),

        setShowLeads: (value) => set((state) => {
          const nextFiltered = applyFilters(state.locations, state.filters, value);
          console.log('[filters] setShowLeads ->', value, 'recomputed=', nextFiltered.length);
          return { showLeads: value, filteredLocations: nextFiltered };
        }),

        setSearchVisible: (visible) =>
          set({ isSearchVisible: visible }),

        setFavoritesVisible: (v) => set({ isFavoritesVisible: !!v }),

        setHomeVisible: (visible) =>
          set({
            isHomeVisible: visible,
            isSearchVisible: false,
            selectedLocation: null,
          }),

        replaySearch: async (entry) => {
          try {
            set({ isSearchVisible: true, isHomeVisible: false });

            const typeRaw = entry?.searchType;    // use as-is
            const value = entry?.searchValue;   // use as-is

            // Prefer explicit backend fields if available
            if (value != null && value !== '') {
              if (typeRaw === 'accountName') {
                return await get().searchByAccountName(value);
              }
              if (typeRaw === 'accountId') {
                return await get().searchByAccountId(value);
              }
              // Any other type (including 'Location') → treat as text/zip search
              return await get().searchByZipCode(value);
            }

            // Fallback: use term directly as text/zip
            const term = entry?.term;
            if (term != null && term !== '') {
              return await get().searchByZipCode(term);
            }

            // Last resort: legacy entries with coordinates
            const c = entry?.center;
            if (c?.lat != null && c?.lng != null) {
              return await get().searchByLatLng(c.lat, c.lng);
            }

            console.warn('[history] replay: no usable fields', entry);
          } catch (e) {
            console.error('[history] replay failed', e);
          }
        },




        searchByZipCode: async (query) => {
          console.log("search by zipcode function triggered with input", query);
          if (!query.trim) {
            set({ error: 'Invalid search query' });
            return;
          }
          try {
            set({
              isLoading: true,
              currentSearchTerm: query,
              isSearchVisible: true,
              error: null,
            });
            console.log("Search Radius", get().searchRadius);

            const radius = get().searchRadius || 5;
            const response = await searchLocations({
              query,
              radius,  // Include radius in the parameters
              ...flattenFilters(get().filtersState), // Include the other filters
            });
            // const response = await searchLocations({ query,...flattenFilters(get().filtersState)});

            if (response.success && response.data?.locations) {
              const { locations, metadata, neighborhoodPolygons } = response.data;
              const [lat, lng] = metadata.searchCenter
                .split(',')
                .map(Number);
              if (isNaN(lat) || isNaN(lng)) {
                throw new Error('Invalid coordinates from API');
              }
              const center = { lat, lng };
              const filteredLocations = enrichWithStacks(applyFilters(
                locations,
                get().filters,
                get().ShowLeads
              ));
              console.log('Filtered after search:', filteredLocations.length, filteredLocations);


              console.log("neighborhoodPolygons outside :", neighborhoodPolygons)

              let mapped;
              if (neighborhoodPolygons?.length) {
                console.log("neighborhoodPolygons called :", neighborhoodPolygons)
                mapped = neighborhoodPolygons.map(p => ({
                  opcoId: p.opcoId,
                  name: p.name,
                  paths: p.polygon.map(([lat, lng]) => ({ lat, lng }))
                }));

                console.log("mapped: ", mapped)
              }

              console.log("mapped: ", mapped)
              set((state) => ({
                locations,
                polygons: mapped || [],
                filteredLocations,
                searchCenter: center,
                isLoading: false,
                error: null,
                searchHistory: [
                  {
                    id: Date.now().toString(),
                    term: String(query),           // value only
                    searchType: 'Location',        // match backend label
                    searchValue: String(query),
                    timestamp: new Date().toISOString(),
                    center,
                    radius: 1,
                    filters: { ...state.filters },
                  },
                  ...state.searchHistory.slice(0, 9),
                ],

              }));



            } else {
              throw new Error('Invalid API response');
            }
          } catch (error) {
            console.error('Search error:', error);
            set({
              isLoading: false,
              error: error.message || 'Search failed',
            });
          }
        },
        searchByAccountId: async (accountId) => {
          if (!accountId?.trim()) {
            set({ error: 'Invalid Account ID' });
            return;
          }

          try {
            set({ isLoading: true, currentSearchTerm: accountId, isSearchVisible: true, error: null });

            const response = await getLocationById(accountId);

            if (response.success && response.data?.locations?.length > 0) {
              const location = response.data.locations[0];
              const center = { lat: location.lat, lng: location.lng };

              set((state) => ({
                locations: [location],
                filteredLocations: [location],
                searchCenter: center,
                selectedLocation: location,
                isLoading: false,
                searchHistory: [
                  {
                    id: Date.now().toString(),
                    term: String(accountId),       // value only
                    searchType: 'accountId',       // match backend label
                    searchValue: String(accountId),
                    timestamp: new Date().toISOString(),
                    center,
                    radius: 1,
                    filters: { ...state.filters },
                  },
                  ...state.searchHistory.slice(0, 9),
                ],
              }));
            } else {
              throw new Error('No location found for this Account ID');
            }
          } catch (error) {
            console.error('Account ID search error:', error);
            set({ isLoading: false, error: error.message || 'Search failed' });
          }
        },

        searchByAccountName: async (accountName) => {
          console.log("Search by Account name entered in MapStore with account name:", accountName);

          if (!accountName?.trim()) {
            set({ error: 'Invalid Account Name' });
            return;
          }

          try {
            set({
              isLoading: true,
              currentSearchTerm: accountName,
              isSearchVisible: true,
              error: null
            });
            const { GlobalDeviceLocation, GlobalMapCenter, filters, searchHistory } = get();
            const userLatLng = GlobalMapCenter;

            console.log("User LatLng for Account Name search:", userLatLng);

            const response = await getLocationById(accountName, 'accountName', userLatLng);

            if (response.success && response.data?.locations?.length > 0) {
              let locations = response.data.locations;



              if (userLatLng?.lat && userLatLng?.lng) {
                locations = locations
                  .filter(loc => loc.lat != null && loc.lng != null)
                  .sort((a, b) => {
                    const distA = Math.hypot(a.lat - userLatLng.lat, a.lng - userLatLng.lng);
                    const distB = Math.hypot(b.lat - userLatLng.lat, b.lng - userLatLng.lng);
                    return distA - distB;
                  });
              }

              const lat = response.data.locations[0].lat;
              const lng = response.data.locations[0].lng;//metadata?.searchCenter?.split(',').map(Number) || [];
              const center = (!isNaN(lat) && !isNaN(lng)) ? { lat, lng } : { lat: locations[0].lat, lng: locations[0].lng };
              const filteredLocations = enrichWithStacks(applyFilters(locations, filters, get().ShowLeads));

              set({
                locations,
                filteredLocations,
                searchCenter: center,
                isLoading: false,
                selectedLocation: (locations.length === 1) ? locations[0] : null,
                searchHistory: [
                  {
                    id: Date.now().toString(),
                    term: String(accountName),     // value only
                    searchType: 'accountName',     // match backend label
                    searchValue: String(accountName),
                    timestamp: new Date().toISOString(),
                    center,
                    radius: 1,
                    filters: { ...filters },
                  },
                  ...searchHistory.slice(0, 9),
                ],
              });
            } else {
              throw new Error('No location found for this Account Name');
            }
          } catch (error) {
            console.error('Account Name search error:', error);
            set({ isLoading: false, error: error.message || 'Search failed' });
          }
        },


        searchByLatLng: async (lat, lng) => {
          set({ searchCenter: { lat, lng } });
          if (!lat || !lng) return;
          console.log("input lat long search center", { lat, lng });
          console.log("Triggering searchByLatLng", get().filtersState);
          console.log("Search Radius latlong search ", get().searchRadius);
          try {
            set({ isLoading: true, error: null });
            const response = await searchLocations({
              lat,
              lng,
              radius: get().searchRadius || 5,
              ...flattenFilters(get().filtersState)
            });

            // console.log('origin', response?.data?.metadata?.origin)
            const detectedBase = response?.data?.metadata?.origin
            if (detectedBase && detectedBase !== get().salesforceBaseUrl) {
              console.log('[SFDC] detected base URL:', detectedBase);
              set({ salesforceBaseUrl: detectedBase.replace(/\/+$/, '') }); // strip trailing slash
            }
            console.log("ORIGIN", get().salesforceBaseUrl)

            if (response.success && response.data?.locations) {


              const { locations, metadata, neighborhoodPolygons } = response.data;
              console.log("neighborhoodPolygons outside :", neighborhoodPolygons)

              //const [lat, lng] = metadata.searchCenter.split(',').map(Number);
              const center = { lat, lng };
              console.log("lat lng search center", center);

              const filteredLocations = enrichWithStacks(applyFilters(locations, get().filters, get().ShowLeads));

              let mapped;

              if (neighborhoodPolygons?.length) {
                console.log("neighborhoodPolygons called :", neighborhoodPolygons)

                mapped = neighborhoodPolygons.map(p => ({
                  opcoId: p.opcoId,
                  name: p.name,
                  paths: p.polygon.map(([lat, lng]) => ({ lat, lng }))
                }));
                console.log("mapped: ", mapped)

              }
              console.log("mapped: ", mapped)


              set((state) => ({
                locations,
                polygons: mapped || [],
                filteredLocations,
                searchCenter: center,
                isLoading: false,
                error: null,
              }));




            } else {
              console.log("query response :", response);
              throw new Error('Invalid API response');
            }
          } catch (error) {
            console.error('LatLng search error:', error);
            set({ isLoading: false, error: error.message || 'Search failed' });
          }
        },

        setFilters: (filters) => {
          if (!filters || typeof filters !== 'object') return;
          const filteredLocations = enrichWithStacks(applyFilters(
            get().locations,
            filters,
            get().ShowLeads
          ));
          set({ filters, filteredLocations });
        },



        clearFilters: () => {
          const defaultFilters = {
            [STATUS.ACTIVE]: true,
            [STATUS.ACTIVE_OLDER]: true,
            [STATUS.PROSPECT]: true,
            [STATUS.TARGET]: true,
            myAccountOnly: false
          };
          const filteredLocations = enrichWithStacks(applyFilters(
            get().locations,
            defaultFilters, get().ShowLeads,
            []
          ));
          set({
            filters: defaultFilters,
            filteredLocations,
            error: null,
          });
        },

        setSelectedLocation: async (location) => {
          try {
            if (!location) {
              set({ selectedLocation: null });
              return;
            }
            // --- stack context (preserve through detail fetch overwrite) ---
            const stackMeta = {
              _stackCount: location?._stackCount,
              _stackMembers: location?._stackMembers,
              _stackSummary: location?._stackSummary,
              _stackPicked: location?._stackPicked,
              _skipRecenter: location?._skipRecenter,


            };


            const result = getLocationId(location);
            console.log("id, type - location id: ", location, result)
            if (!result) return;

            const { id, type } = result;
            const isLead = (location?.scope ?? '').toUpperCase() === 'SALESFORCE' && (location?.shipStatus ?? '').toUpperCase() === 'TARGET';

            console.log("isLead", isLead);
            // For leads, we skip the detailed fetch and use the provided data directly
            let response;
            if (isLead) {
              response = { success: true, data: { locations: [location] } };
            }
            else {
              response = await getLocationById(id, type);
            }

            if (response.success && response.data?.locations?.[0]) {
              const detailedLocation = response.data.locations[0];

              const selected = {
                ...detailedLocation,
                lat: location?.lat ?? detailedLocation?.lat,
                lng: location?.lng ?? detailedLocation?.lng,
              };

              const selectedWithStack = { ...selected, ...stackMeta };

              const newId = getLocationId(detailedLocation)?.id || '';
              const newName = (detailedLocation.name || '').trim().toLowerCase();

              set((state) => ({
                selectedLocation: selectedWithStack,
                error: null,
                recentlyViewed: [
                  detailedLocation,
                  ...state.recentlyViewed.filter((loc) => {
                    const sameId = newId && getLocationId(loc)?.id === newId;
                    const sameName =
                      newName &&
                      (loc.name || '').trim().toLowerCase() === newName;
                    return !(sameId || sameName);
                  }),
                ].slice(0, 5),

                isHomeVisible: false,
                isSearchVisible: true,
              }));

              const isGoogleTarget = detailedLocation?.status || detailedLocation?.shipStatus === "target" &&
                detailedLocation?.scope === "GOOGLE";

              const feedbackId = detailedLocation?.placeId;

              console.log("[feedback] selection setter", { detailedLocation, feedbackId, isGoogleTarget });

              if (isGoogleTarget && feedbackId) {
                const alreadyLoaded = !!get().feedbackById?.[feedbackId];
                console.log("[feedback] selection setter", { feedbackId, alreadyLoaded });

                if (!alreadyLoaded) {
                  get().fetchFeedback(feedbackId);
                }
              }
              console.log("Updated recentlyViewed:", get().recentlyViewed.map(l => getLocationId(l)?.id));
            }
          } catch (error) {
            console.error('Error setting location:', error);
            set({
              selectedLocation: location,
              error:
                error.message || 'Failed to load location details',
            });
          }
        },

        setSearchRadius: (radius) => {
          if (typeof radius !== 'number' || radius < 1) return;
          set({ searchRadius: radius });
        },

        clearError: () => set({ error: null }),

        reset: () => set(initialState),
      };
    },
    {
      name: 'map-store',

      onRehydrateStorage: () => {
        console.log('[Zustand] Rehydrating…');

        // 2nd-stage callback: runs after storage has been merged
        return (state, error) => {
          console.log('[Zustand] Rehydrated.');
          setHasHydratedGlobal();

          // state === hydrated store snapshot  → has all actions
          if (state && typeof state.loadUserPrefs === 'function') {
            console.log('[rehydrate] calling loadUserPrefs…');
            state.loadUserPrefs();          // ← fire the GET /api/v1/user here
          } else {
            console.warn('[rehydrate] loadUserPrefs not found in state!');
          }
        };
      },


      partialize: (state) => ({
        searchHistory: state.searchHistory,
        recentlyViewed: state.recentlyViewed,
        filters: state.filters,
        searchRadius: state.searchRadius,
        locations: state.locations,
        polygons: state.polygons,
        hasHydrated: state.hasHydrated,
        favoritesIds: state.favoritesIds,
        serverFavoritesRaw: state.serverFavoritesRaw,
        salesforceBaseUrl: state.salesforceBaseUrl,
      }),
    }
  )
);


// --- STACK ENRICHMENT (Option A) ---
// Takes the already-visible list (post-filters) and attaches:
// - _stackCount
// - _stackMembers (all visible accounts at same lat/lng)
// - _stackSummary (3-bucket counts for UI blips)
const enrichWithStacks = (visibleList) => {
  if (!Array.isArray(visibleList) || visibleList.length === 0) return visibleList || [];

  // Group by exact coordinate string
  const keyOf = (loc) => {
    const lat = loc?.lat;
    const lng = loc?.lng;
    if (lat == null || lng == null) return null;
    return `${lat},${lng}`;
  };

  const groups = new Map();
  for (const loc of visibleList) {
    const key = keyOf(loc);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(loc);
  }

  // 3-bucket summary helper (minimal + stable)
  // Uses (shipStatus ?? status) because your code already uses that pattern elsewhere.
  // --- stack classification helper (FULL + correct) ---
  const bucketOf = (r) => {
    const ship = (r?.shipStatus || '').toUpperCase();
    const status = (r?.status || '').toUpperCase();

    const bu =
      r?.accountDetails?.businessUnit ||
      r?.accountDetails?.business_unit ||
      r?.businessUnit ||
      r?.business_unit ||
      '';

    // Priority matters
    if (ship === 'TARGET' || status === 'TARGET') return 'TARGET';

    // Specialty (non US Broadline)
    if (bu && bu !== 'US Broadline') return 'SPECIALTY';

    // Active Older
    if (ship === 'ACTIVEOLDER' || status === 'ACTIVEOLDER') return 'ACTIVEOLDER';

    // Prospect
    if (ship === 'PROSPECT' || status === 'PROSPECT') return 'PROSPECT';

    // Default
    return 'ACTIVE';
  };


  // Build collapsed output: ONE rep per coordinate key
  const out = [];
  const usedKeys = new Set();

  let stackedReps = 0;

  for (const rep of visibleList) {
    const key = keyOf(rep);

    // If no lat/lng, keep it as-is (rare, but safe)
    if (!key) {
      out.push(rep);
      continue;
    }

    // Skip duplicates for the same coordinate
    if (usedKeys.has(key)) continue;
    usedKeys.add(key);

    const members = groups.get(key) || [rep];
    const count = members.length;

    if (count <= 1) {
      out.push(rep);
      continue;
    }

    // Build 3-bucket summary
    const summary = { ACTIVE: 0, PROSPECT: 0, TARGET: 0, SPECIALTY: 0, ACTIVEOLDER: 0 };
    for (const m of members) summary[bucketOf(m)]++;

    stackedReps++;

    out.push({
      ...rep,
      _stackCount: count,
      _stackMembers: members,
      _stackSummary: summary,
    });
  }

  // Console log checkpoint (only when something is stacked)
  if (stackedReps > 0) {
    const example = out.find((x) => x?._stackCount > 1);
    console.log("[stack] collapsed:", {
      inLen: visibleList.length,
      outLen: out.length,
      stackedReps,
      example: example
        ? {
          name: example?.name ?? example?.companyName,
          lat: example?.lat,
          lng: example?.lng,
          _stackCount: example?._stackCount,
          _stackSummary: example?._stackSummary,
        }
        : null,
    });
  }

  return out;
};

export const applyFilters = (locations, filters = [], showLeads = true) => {
  if (!Array.isArray(locations)) return [];
  if (filters.myAccountOnly) console.log('My Account Only filter is ON');

  const uniqueIds = new Set();
  let leadsRemoved = 0;

  const isBUFilterActive = () => {
    try {
      // console.log('[filters][BU] checking if BU filter is active', useMapStore.getState()?.filtersState);

      const bu = useMapStore.getState()?.filtersState?.businessUnit || [];
      return Array.isArray(bu) && bu.length < 8;
    } catch {
      return false;
    }
  };

  const isTargetRow = (loc) =>
    ((loc.shipStatus || loc.status || '').toLowerCase() === 'target');

  const isSalesforceLeadLocal = (loc) => {
    const status = (loc.shipStatus || loc.status || '').toLowerCase();
    return status === 'target' && loc.scope === 'SALESFORCE';
  };

  const out = locations.filter((location) => {
    // ---- de-dupe by robust id ----
    const idObj = getLocationId(location);
    const locationId = idObj?.id;
    if (!locationId || uniqueIds.has(locationId)) return false;
    uniqueIds.add(locationId);

    // ---- optional global "hide leads" toggle ----
    if (!showLeads && isSalesforceLeadLocal(location)) {
      leadsRemoved++;
      return false;
    }

    // ---- "My Account Only" filter ----
    if (filters.myAccountOnly && location.userAccount !== true) return false;

    // ---- Business Unit logic (runs only when BU pills are active) ----
    if (isBUFilterActive()) {
      // A) Hide ALL targets if BU filter is active (targets don't have BU)
      if (isTargetRow(location)) {
        console.log('[filters][BU] Hiding TARGET because BU filter is active →',
          location?.name || location?.accountName);
        return false;
      }

      // B) Extract BU from common shapes
      const bu =
        (location.businessUnit?.trim?.()) ||
        (location.business_unit?.trim?.()) ||
        (location.accountDetails?.businessUnit?.trim?.()) ||
        '';

      // C) Drop non-target rows that still have no BU
      if (!bu) {
        console.log('[filters][BU] No BU on non-target → dropping',
          location?.name || location?.accountName);
        return false;
      }

      // D) Require BU ∈ selected set
      const selectedBUs = useMapStore.getState()?.filtersState?.businessUnit || [];
      if (!selectedBUs.includes(bu)) {
        return false;
      }
    }

    // ---- existing status checks ----
    const statusKey = location.shipStatus;
    const statusMatch = filters[statusKey] ?? true;
    return statusMatch;
  });

  if (!showLeads) {
    console.log('[filters] applyFilters: leads removed =', leadsRemoved, 'final count =', out.length);
  }
  return out;
};
